import { normalizeStaffName } from './csvParser.js'

// Shared shift→staff name matcher.
//
// Extracted from ScheduleImportModal so the photo-import flow
// (RosterScanModal) and the CSV/ICS import flow use the SAME matching
// rules. The heuristic is deliberately the union of:
//   1. exact case-insensitive match (normalized whitespace + case), then
//   2. substring match — staff name contains the import name OR vice
//      versa (so an ICS event "Sarah - cook" matches staff "Sarah Lopez").
//
// `normalizeStaffName` (trim → lowercase → collapse whitespace) is the
// single normalization used on BOTH sides so the two callers can never
// drift apart on what counts as "the same name".

export { normalizeStaffName }

// Match ONE name against the active staff list. Returns the matched
// staff object + the match kind, or null. Pure — no I/O.
//   activeStaff: [{ id, name, ... }, ...]
//   name:        string read off the artifact / import row
// → { staff, match: 'exact' | 'fuzzy' } | null
export function matchStaffName(name, activeStaff) {
  const lower = normalizeStaffName(name)
  if (!lower) return null
  const list = activeStaff ?? []

  // 1. Exact case-insensitive match.
  for (const s of list) {
    if (normalizeStaffName(s.name) === lower) {
      return { staff: s, match: 'exact' }
    }
  }
  // 2. Substring either direction.
  for (const s of list) {
    const sl = normalizeStaffName(s.name)
    if (!sl) continue
    if (sl.includes(lower) || lower.includes(sl)) {
      return { staff: s, match: 'fuzzy' }
    }
  }
  return null
}

// Build the dedupe key for a schedule_assignments row. Times are
// normalized to HH:MM so "09:00" and "09:00:00" collapse to one key —
// the SAME key shape used when reading existing rows back out.
//   (staff_id, shift_date, start_time) → "staff|date|HH:MM"
export function assignmentDedupeKey(staffId, shiftDate, startTime) {
  return `${staffId}|${shiftDate}|${(startTime || '').slice(0, 5)}`
}
